/**
 * プランの共有設定
 *
 * 共有先はチェックボックスで選ぶ。プランは複製されず同じものを見るので、
 * 相手には閲覧とスタンプだけを許し、条件やスポットの変更はオーナーに残る。
 * 複数のプランをまとめて開いたときは、一部のプランだけに共有している人を「そのまま」にしておける。
 */

import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { getRoutePlanShareCandidates, getRoutePlanShares, updateRoutePlanShares } from '@/api/services/route-plan'
import type { ShareCandidate } from '@/api/types/route-plan'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { apiErrorMessage } from '@/lib/api-error'

/** all = 全プランに共有 / none = どのプランにも共有しない / keep = 一部のまま触らない */
type ShareState = 'all' | 'none' | 'keep'

type Props = {
  planIds: number[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: () => void
}

export const PlanShareSheet = ({ planIds, open, onOpenChange, onSaved }: Props) => {
  const [candidates, setCandidates] = useState<ShareCandidate[]>([])
  // ユーザーごとの、共有しているプランの件数
  const [sharedCounts, setSharedCounts] = useState<Map<string, number>>(new Map())
  const [states, setStates] = useState<Map<string, ShareState>>(new Map())
  const [isLoading, setIsLoading] = useState(false)
  const { isSubmitting: isSaving, run } = useSubmitLock()
  const total = planIds.length
  // 呼び出し側が毎回新しい配列を渡しても取り直さないよう、中身で比べる
  const planKey = planIds.join(',')

  const initialState = (userId: string): ShareState => {
    const count = sharedCounts.get(userId) ?? 0
    if (count === 0) return 'none'
    return count === total ? 'all' : 'keep'
  }

  useEffect(() => {
    if (!open || planKey === '') return
    const controller = new AbortController()
    setIsLoading(true)
    Promise.all([
      getRoutePlanShareCandidates(controller.signal),
      getRoutePlanShares(planKey.split(',').map(Number), controller.signal),
    ])
      .then(([users, shares]) => {
        setCandidates(users)
        setSharedCounts(new Map(shares.map((share) => [share.user_id, share.plan_ids.length])))
        setStates(new Map())
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        toast.error(apiErrorMessage(error, '共有先の取得に失敗しました'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [open, planKey])

  const stateOf = (userId: string): ShareState => states.get(userId) ?? initialState(userId)

  const toggle = (userId: string) =>
    setStates((prev) => new Map(prev).set(userId, stateOf(userId) === 'all' ? 'none' : 'all'))

  const handleSave = async () => {
    const changed = (state: ShareState) =>
      candidates.filter((c) => stateOf(c.id) === state && initialState(c.id) !== state).map((c) => c.id)
    const addUserIds = changed('all')
    const removeUserIds = changed('none')
    if (addUserIds.length === 0 && removeUserIds.length === 0) {
      onOpenChange(false)
      return
    }
    await run(async () => {
      try {
        await updateRoutePlanShares({ plan_ids: planIds, add_user_ids: addUserIds, remove_user_ids: removeUserIds })
        toast.success('共有設定を保存しました')
        onOpenChange(false)
        onSaved?.()
      } catch (error) {
        toast.error(apiErrorMessage(error, '共有の保存に失敗しました'))
      }
    })
  }

  return (
    <BottomSheet
      title={total > 1 ? `${total} 件のプランを共有` : 'プランを共有'}
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={[0.7]}
    >
      <div className="space-y-4 px-4 pb-6">
        <p className="text-xs text-muted-foreground">
          共有した人はプランの閲覧とスタンプ押しだけができます。条件やスポットの変更はできません
        </p>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={20} className="animate-spin text-muted-foreground" />
          </div>
        ) : candidates.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">共有できるユーザーがいません</p>
        ) : (
          <div className="space-y-1">
            {candidates.map((candidate) => {
              const state = stateOf(candidate.id)
              return (
                <label
                  key={candidate.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-accent/50"
                >
                  <Checkbox checked={state === 'all'} onCheckedChange={() => toggle(candidate.id)} />
                  <span className="min-w-0 flex-1 truncate text-sm">{candidate.name}</span>
                  {state === 'keep' && (
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {sharedCounts.get(candidate.id)}/{total} 件で共有中
                    </span>
                  )}
                </label>
              )
            })}
          </div>
        )}

        <Button className="w-full" disabled={isLoading || isSaving} onClick={() => void handleSave()}>
          {isSaving ? <Loader2 size={16} className="animate-spin" /> : '保存'}
        </Button>
      </div>
    </BottomSheet>
  )
}
